import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Formik } from 'formik';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useNotificationsContext } from '../context/NotificationsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FilterSelect from '../../_common/components/FilterSelect';
import { FilterFromDate, FilterToDate } from '../../_common/components/FilterDateRange';
import FiltersBar from '../../_common/components/FiltersBar';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/notificationsActions';
import { RequestCancellationManager } from '../../_common/utils/RequestCancellationManager';
import { generateExcelFile } from '../../_common/utils/excel';
import { notificationsHeader } from '../../_common/constants/exportedFilesMainHeaders';
import { headersNotifications } from '../../_common/constants/exportedFilesHeaders';
import { sendNotificationStatusOptions, sendNotificationTypeOptions } from '../../_common/constants/options';
import { getType } from '../utils/getType';
import { getStatus } from '../utils/getStatus';
import { getStatusText } from '../utils/getStatusText';

type QueryParams = Record<string, any>

type FilterValues = {
  fromDate: string | Date
  toDate: string | Date
  type: string
  status: string
  agentID: string
  agentName: string
  customerName: string
}

type NotificationExcelItem = {
  sentAt?: string
  subject?: string
  type?: string
  status?: string
  statusText?: string
  target?: string
  sender?: {
    agentID?: string
    firstName?: string
    lastName?: string
  }
  receiver?: {
    agentID?: string
    firstName?: string
    lastName?: string
  }
}

const prepareFilter = (queryParams: QueryParams, values: FilterValues): QueryParams => {
  const {
    fromDate,
    toDate,
    type,
    status,
    agentID,
    agentName,
    customerName
  } = values

  const newQueryParams = { ...queryParams }
  const filter: QueryParams = {}

  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD')
  } else {
    delete newQueryParams.fromDate
  }

  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD')
  } else {
    delete newQueryParams.toDate
  }

  if (Boolean(type)) {
    filter.type = type
  } else {
    delete newQueryParams.type
  }

  if (Boolean(status)) {
    filter.status = status
  } else {
    delete newQueryParams.status
  }

  if (Boolean(agentID)) {
    filter.agentID = agentID
  } else {
    delete newQueryParams.agentID
  }

  if (Boolean(agentName)) {
    filter.agentName = agentName
  } else {
    delete newQueryParams.agentName
  }

  if (Boolean(customerName)) {
    filter.customerName = customerName
  } else {
    delete newQueryParams.customerName
  }

  return { ...newQueryParams, ...filter }
}

export function NotificationsFilter(): JSX.Element {
  const dispatch: any = useDispatch()
  const intl = useIntl()

  const notificationsContext: any = useNotificationsContext()

  const notificationsProps = useMemo(() => {
    return {
      queryParams: notificationsContext.queryParams,
      setQueryParams: notificationsContext.setQueryParams
    }
  }, [notificationsContext])

  const applyFilter = (values: FilterValues): void => {
    RequestCancellationManager.stopRequests(actions.geNotificationsListIDPrefix)

    const newQueryParams = prepareFilter(notificationsProps.queryParams, values)

    if (!isEqual(newQueryParams, notificationsProps.queryParams)) {
      notificationsProps.setQueryParams(newQueryParams)
    }
  }

  const qs = new URLSearchParams(window.location.search)

  const fromDate: string | Date = qs.get('fromDate') || ''

  const toDate: string | Date = qs.get('toDate')
    ? moment(qs.get('toDate') as string).subtract(1, 'days').toDate()
    : ''

  const type: string = qs.get('type') || ''
  const status: string = qs.get('status') || ''
  const agentID: string = qs.get('agentID') || ''
  const agentName: string = qs.get('agentName') || ''
  const customerName: string = qs.get('customerName') || ''

  const [loading, setLoading] = useState<boolean>(false)

  const handleExport = (): void => {
    setLoading(true)

    dispatch(actions.getNotificationsForExcelFile(notificationsProps.queryParams))
      .then(prepareDataForExcel)
      .then((data: any) =>
        generateExcelFile(data, 'Notifications.xlsx', notificationsHeader)
      )
      .catch((error: unknown) => {
        console.error(`Error: ${error}`)
        setLoading(false)
      })
      .finally(() => setLoading(false))
  }

  const prepareDataForExcel = (data: NotificationExcelItem[]) => {
    return data.map((item, index) => {
      const {
        sentAt,
        subject,
        type,
        status,
        statusText,
        target,
        sender,
        receiver
      } = item as NotificationExcelItem

      return {
        [headersNotifications[0]]: index + 1,
        [headersNotifications[1]]: sentAt
          ? moment(sentAt).format('DD.MM.YYYY HH:mm:ss')
          : '',
        [headersNotifications[2]]: subject,
        [headersNotifications[3]]: type ? getType(type) : '',
        [headersNotifications[4]]: target,
        [headersNotifications[5]]: status ? getStatus(status) : '',
        [headersNotifications[6]]: statusText ? getStatusText(statusText) : '',
        [headersNotifications[7]]: sender?.agentID || '',
        [headersNotifications[8]]: [sender?.firstName, sender?.lastName].filter(Boolean).join(' '),
        [headersNotifications[9]]: receiver?.agentID ?? '',
        [headersNotifications[10]]: [receiver?.firstName, receiver?.lastName].filter(Boolean).join(' '),
      }
    })
  }

  const prefix = 'notifications-filter'
  const containerClassnames = 'w-200px position-relative my-1 px-3'

  return (
    <Formik<FilterValues>
      initialValues={{
        fromDate,
        toDate,
        type,
        status,
        agentID,
        agentName,
        customerName
      }}
      onSubmit={applyFilter}
      enableReinitialize
    >
      {({ values, ...formikProps }) => (
        <FiltersBar
          filters={
            <div className="d-flex flex-wrap justify-content-center align-items-center mb-0 w-100">
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName="type"
                valueName={values.type}
                options={sendNotificationTypeOptions}
                placeholder={intl.formatMessage({
                  id: `${prefix}-type`,
                  defaultMessage: 'Type'
                })}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName="status"
                valueName={values.status}
                options={sendNotificationStatusOptions}
                placeholder={intl.formatMessage({
                  id: `${prefix}-status`,
                  defaultMessage: 'Result'
                })}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentID"
                placeholder={intl.formatMessage({
                  id: `${prefix}-agentID`,
                  defaultMessage: 'Agent ID'
                })}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentName"
                placeholder={intl.formatMessage({
                  id: `${prefix}-agentName`,
                  defaultMessage: 'Agent name'
                })}
                value={values.agentName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerName"
                placeholder={intl.formatMessage({
                  id: `${prefix}-customerName`,
                  defaultMessage: 'Customer name'
                })}
                value={values.customerName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={<ButtonExportToExcel exportFunc={handleExport} loading={loading}/>}
        />
      )}
    </Formik>
  )
}